import * as Sentry from "@sentry/react";
import { createRoot } from 'react-dom/client'
import './styles/app.css'
import App from './App.tsx'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
// Removed BrowserRouter import as it's now handled in App.tsx
// Removed AuthProvider import as AuthContext.Provider is handled in App.tsx
// Removed WebSocketProvider import - now only in App.tsx where it has access to AuthContext
import {QueryClient, QueryClientProvider} from "@tanstack/react-query";
import axios from 'axios';
import { ThemeProvider } from './context/ThemeContext.tsx';
import { installSessionExpiryInterceptor } from './lib/sessionExpiry.ts';
import { installWrongContextInterceptor } from './lib/wrongContext.ts';
import { installAccountSuspensionInterceptor } from './lib/accountSuspension.ts';

// Segment events by deployment. The per-environment marker (VITE_APP_ENV_LABEL) is
// injected at runtime via window.APP_CONFIG (empty in production); read it directly
// rather than via ./config so Sentry still initializes if config loading throws.
const sentryEnvLabel =
  window.APP_CONFIG?.VITE_APP_ENV_LABEL ||
  (import.meta.env.VITE_APP_ENV_LABEL) ||
  "";
Sentry.init({
  dsn: "https://aa82de1d1d71d5ce0258afadaa49f50f@sentry.witinaai.com/7",
  // Don't report from local `vite dev` (import.meta.env.DEV) — only real deployments.
  enabled: !import.meta.env.DEV,
  environment: sentryEnvLabel ? sentryEnvLabel.toLowerCase() : "production",
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
  ],
  // Tracing
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: ["localhost", /^https:\/\/[^/]*\.witinaai\.com/],
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});


const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // A 401 is parked by the session-expiry interceptor rather than rejected,
      // so it normally never reaches here; this covers the endpoints the
      // interceptor deliberately lets through (everything under /api/auth/),
      // where retrying an auth failure just triples the noise.
      retry: (failureCount, error) =>
        !(axios.isAxiosError(error) && error.response?.status === 401) && failureCount < 3,
    },
  },
})

// Installed at module scope so it is in place before the first request leaves
// the app. `isAuthenticated` reads the cache directly: a 401 only means the
// session expired if we believed we had one.
installSessionExpiryInterceptor({
  isAuthenticated: () => queryClient.getQueryData(['currentUser']) != null,
})
installAccountSuspensionInterceptor()
// Turns a "you hold this permission, but not in this context" 403 into an offer
// to switch, rather than a dead end (see lib/wrongContext).
installWrongContextInterceptor()
// NOTE: <StrictMode> is applied inside App.tsx, just below <BrowserRouter>,
// rather than here — keeping the router outside StrictMode avoids dev-only
// double history pushes while the whole app tree still gets StrictMode checks.
// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
createRoot(document.getElementById('root')!).render(
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <App />
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </ThemeProvider>,
)
